// react
import { useState } from 'react';

// types
import { Article } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink } from 'react-router-dom';

// mui
import { Box, Button, Collapse, Grid, Link, Paper, Typography } from '@mui/material';

// ui
import { Html } from '../../ui/Html/Html';
import { Time } from '../../ui/Time/Time';

// theme
import { theme } from '../../../theme';

// utils
import { translateCategory } from '../../../utils/translations';

// props
export interface CardArticleProps {
	article: Article;
	isFeatured?: boolean;
}

export const CardArticle = ({ article, isFeatured = false }: CardArticleProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [isExpanded, setIsExpanded] = useState(false);

	// vars
	const sxImg = {
		borderRadius: 2,
		display: 'block',
		width: '100%',
	};

	return (
		<Paper
			sx={{
				borderColor: isExpanded ? 'brand.blue.500' : 'transparent',
				borderStyle: 'solid',
				borderWidth: 1,
				height: '100%',
				padding: theme.spacing(2),
			}}>
			<Grid
				container
				direction="column"
				justifyContent="space-between"
				spacing={2}
				sx={{ height: `calc(100% + ${theme.spacing(2)})` }}>
				<Grid item>
					<Grid container spacing={2}>
						<Grid item>
							<Grid alignItems="flex-start" container justifyContent="space-between" spacing={1}>
								<Grid item xs={true}>
									{article.release && (
										<Typography
											color="brand.blue.400"
											component="div"
											fontWeight={800}
											variant="h4">
											{article.release}
										</Typography>
									)}
									<Typography fontWeight={700} variant="h2">
										{article.title}
									</Typography>
									<Time date={article.date} />
								</Grid>
							</Grid>
						</Grid>
						<Grid item>
							<Grid container spacing={2}>
								{article.img.src && (
									<Grid item xs={12}>
										<Box component="img" src={article.img.src} alt={article.img.alt} sx={sxImg} />
									</Grid>
								)}
								{article.intro.length > 0 && (
									<Grid item xs={12}>
										<Box sx={{ '& img': sxImg }}>
											<Html html={article.intro.join('')} />
										</Box>
									</Grid>
								)}
								{article.content.length > 0 && (
									<>
										{!isFeatured && (
											<Grid item xs={12}>
												<Button
													onClick={() => setIsExpanded(!isExpanded)}
													size="small"
													variant="outlined">
													{isExpanded ? t('readLess') : t('readMore')}
												</Button>
											</Grid>
										)}
										<Grid item xs={12}>
											<Collapse in={isFeatured || isExpanded}>
												<Box sx={{ '& img': sxImg }}>
													<Html html={article.content.join('')} />
												</Box>
											</Collapse>
										</Grid>
									</>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<Grid item>
					<Grid container>
						<Grid item xs={12}>
							<Typography fontWeight={600} gutterBottom>
								{t('categories')}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<Grid container spacing={1}>
								{article.categories.map((category, i) => (
									<Grid item key={`article-category-${i}`}>
										<Link
											color="primary"
											component={RouterLink}
											to={`/features?category=${category}`}
											underline="hover">
											{translateCategory(category, t)}
										</Link>
									</Grid>
								))}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};
